import { getDatabase } from './db'
import { loadStatements, storeStatements } from '../contextsV2/store'
import type { RemoteStatement } from '../contextsV2/statements'
import { manualContextKey, signalChange, type ManualContext } from '../intelligence/contextLab/manualContexts'

/**
 * Contexts in the quick local recovery copy (pre-launch C3). The copy carries the person's live Contexts (name, purpose, notes) and every
 * act of theirs in them, without the account identifier (the copy never holds raw account ids: each Context is re-owned on import). Restoring is
 * additive and never replaces: a Context or an act that already exists is left exactly as it is.
 */
export type RecoveredContext = Omit<ManualContext, 'ownerId'>
export interface RecoveredContexts { contexts: RecoveredContext[]; contextStatements: RemoteStatement[] }

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const KINDS = new Set(['done', 'date', 'chosen', 'hidden', 'hidden_item'])

export const isRecoveredContext = (value: unknown): value is RecoveredContext =>
  isRecord(value) && value.version === 1 && typeof value.id === 'string' && typeof value.title === 'string' && Array.isArray(value.links)
  && typeof value.createdAt === 'number' && !('ownerId' in value)
export const isRecoveredStatement = (value: unknown): value is RemoteStatement =>
  isRecord(value) && typeof value.id === 'string' && typeof value.contextId === 'string' && typeof value.kind === 'string' && KINDS.has(value.kind)
  && isRecord(value.anchor) && typeof value.at === 'string' && 'value' in value

export function packRecoverableContexts(contexts: readonly ManualContext[], statements: readonly (RemoteStatement & { pending?: boolean })[]): RecoveredContexts {
  return {
    contexts: contexts.filter(context => !context.archivedAt).map(({ ownerId: _owner, ...rest }) => rest),
    contextStatements: statements.map(({ pending: _pending, ...rest }) => rest as RemoteStatement),
  }
}

export async function restoreRecoveredContexts(recovered: Partial<RecoveredContexts>, owner: string): Promise<{ contexts: number; statements: number }> {
  const database = await getDatabase()
  let contexts = 0
  for (const context of recovered.contexts ?? []) {
    const key = manualContextKey(owner, context.id)
    if (await database.get('meta', key)) continue
    await database.put('meta', { key, value: JSON.stringify({ ...context, ownerId: owner }) })
    contexts++
  }
  const have = new Set((await loadStatements(owner).catch(() => [])).map(statement => statement.id))
  const fresh = (recovered.contextStatements ?? []).filter(statement => !have.has(statement.id))
  // Pending, so they are sent to the account like any act made on this device.
  if (fresh.length) await storeStatements(owner, fresh.map(statement => ({ ...statement, pending: true })))
  if (contexts || fresh.length) signalChange(owner)
  return { contexts, statements: fresh.length }
}
